import { AddressType, RatesType } from '../../../store/reducers/GetRates/Type';
import { BoxType, RatesInputType, WeightUnitType } from '../Type';
import { defaultBox, SHIPMENT_TYPES } from '../Constant';
import { calculateVolume } from '../../../services/calculateVolume';
import { inputRatesSchema } from '../RatesInput/Schema';
import API from '@Services/apiAxios';

export interface ExtractionPollingOptions {
  onStatusChange?: (status: string) => void;
  isCancelled?: () => boolean;
  registerTimer?: (timer: NodeJS.Timeout | null) => void;
  pollIntervalMs?: number;
}

export interface InvalidFieldItem {
  field?: string;
  reason?: string;
  [key: string]: any;
}

export interface GetRatesExtractionResponse {
  jobId?: string;
  status?: string;
  documentType?: string;
  confidence?: number;
  invalidFields?: (string | InvalidFieldItem)[];
  error?: string | null;
  fields?: any;
  [key: string]: any;
}

/**
 * Format invalidFields into a human-readable, actionable message.
 * Extracts `reason` or `field` from objects, handles string items, and eliminates "[object Object]".
 */
export const formatInvalidFieldsReason = (invalidFields?: (string | InvalidFieldItem)[]): string => {
  if (!Array.isArray(invalidFields) || invalidFields.length === 0) return '';

  const messages: string[] = [];

  for (const item of invalidFields) {
    if (!item) continue;
    if (typeof item === 'string') {
      messages.push(item);
    } else if (typeof item === 'object') {
      if (item.reason && typeof item.reason === 'string') {
        messages.push(item.reason);
      } else if (item.field && typeof item.field === 'string') {
        messages.push(`Validation issue on ${item.field}`);
      } else {
        const str = JSON.stringify(item);
        if (str !== '{}') messages.push(str);
      }
    }
  }

  return messages.join('; ');
};

/**
 * Safely unwrap extraction response object whether returned at root level
 * or wrapped inside response.data.data
 */
export const unwrapExtractionResponse = (raw: any): GetRatesExtractionResponse => {
  if (!raw) return {};
  if (raw.jobId || raw.status || raw.fields) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object') {
    if (raw.data.jobId || raw.data.status || raw.data.fields) {
      return {
        ...raw.data,
        error: raw.data.error || raw.error || null,
      };
    }
  }
  return raw;
};

/**
 * Trigger async document extraction with documentType = "shipment" and mode = "async"
 * POST /speedboxbot/extract
 */
export const extractShipmentDocumentAsync = async (s3Key: string): Promise<GetRatesExtractionResponse> => {
  const { data } = await API.post<GetRatesExtractionResponse>('/speedboxbot/extract', {
    documentType: 'shipment',
    s3Key,
    mode: 'async',
  });
  return unwrapExtractionResponse(data);
};

/**
 * Trigger async text extraction with documentType = "shipment" and mode = "async"
 * POST /speedboxbot/extract
 */
export const extractShipmentTextAsync = async (textContent: string): Promise<GetRatesExtractionResponse> => {
  const { data } = await API.post<GetRatesExtractionResponse>('/speedboxbot/extract', {
    documentType: 'shipment',
    textContent,
    mode: 'async',
  });
  return unwrapExtractionResponse(data);
};

/**
 * Poll job status
 * GET /speedboxbot/extract/{jobId}
 */
export const getShipmentExtractionStatus = async (jobId: string): Promise<GetRatesExtractionResponse> => {
  const { data } = await API.get<GetRatesExtractionResponse>(`/speedboxbot/extract/${jobId}`);
  return unwrapExtractionResponse(data);
};

/**
 * Poll extraction job until terminal status (completed, needs_review, failed)
 * Waits 10 seconds before initial poll and between consecutive polls.
 */
export const pollShipmentExtraction = async (
  initialResponse: GetRatesExtractionResponse,
  options: ExtractionPollingOptions = {},
): Promise<GetRatesExtractionResponse> => {
  const { onStatusChange, isCancelled, registerTimer, pollIntervalMs = 10000 } = options;

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // If already terminal (e.g. sync fallback)
  const initialStatus = initialResponse.status;
  if (initialStatus === 'completed' || initialStatus === 'failed' || initialStatus === 'needs_review') {
    return initialResponse;
  }

  const jobId = initialResponse.jobId;
  if (!jobId) {
    if (initialResponse.fields && Object.keys(initialResponse.fields).length > 0) {
      return initialResponse;
    }
    return {
      status: 'failed',
      error: initialResponse.error || 'Failed to initialize extraction: missing jobId in response',
    };
  }

  let currentStatus = initialStatus || 'pending';

  while (currentStatus === 'pending' || currentStatus === 'processing') {
    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.(
      currentStatus === 'processing'
        ? 'AI is analyzing shipment details... (processing)'
        : 'AI extraction in progress... (waiting for results)',
    );

    // Wait 10 seconds before the poll without overlapping requests
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        registerTimer?.(null);
        if (isCancelled?.()) {
          reject(new Error('Extraction cancelled'));
        } else {
          resolve();
        }
      }, pollIntervalMs);
      registerTimer?.(timer);
    });

    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.('Checking extraction status...');
    const pollResult = await getShipmentExtractionStatus(jobId);

    currentStatus = pollResult.status || 'failed';

    if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'needs_review') {
      return pollResult;
    }
  }

  return {
    status: currentStatus,
    error: `Unexpected extraction status: ${currentStatus}`,
  };
};

export const isExcelFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return (
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel'
  );
};

export interface MappedGetRatesData {
  pickup: AddressType & Record<string, any>;
  destination: AddressType & Record<string, any>;
  pickupPinCode: string;
  destinationPinCode: string;
  shipmentType: string;
  weight: number;
  weightUnit: WeightUnitType;
  boxes: BoxType[];
  hideBoxes: boolean;
  boxCount: number;
  chargeableWeight: number;
  isValid: boolean;
  missingFields: string[];
}

export const mapAiResponseToGetRates = (
  fields: any,
  countries: any[] = [],
  countryList: any[] = [],
): MappedGetRatesData => {
  const getCountryName = (cName?: string) => {
    if (!cName) return '';
    const trimmed = String(cName).trim();
    const matchCountry = (countries || []).find(
      c =>
        c.name.toLowerCase() === trimmed.toLowerCase() ||
        (c.alternateNames && c.alternateNames.some((alt: string) => alt.toLowerCase() === trimmed.toLowerCase())) ||
        (c.code && c.code.toLowerCase() === trimmed.toLowerCase()),
    );
    if (matchCountry) return matchCountry.name;

    const listMatch = (countryList || []).find(
      c => c.value.toLowerCase() === trimmed.toLowerCase() || c.display.toLowerCase() === trimmed.toLowerCase(),
    );
    if (listMatch) return listMatch.value;

    return trimmed;
  };

  const inferCountry = (cName?: string, _city?: string, pincode?: string) => {
    if (cName) {
      const resolved = getCountryName(cName);
      if (resolved) return resolved;
    }
    const cleanPin = String(pincode || '').trim();
    if (/^\d{6}$/.test(cleanPin)) {
      const match = (countries || []).find(c => c.name?.toLowerCase() === 'india');
      if (match) return match.name;
      return 'India';
    }
    const match = (countries || []).find(c => c.name?.toLowerCase() === 'india');
    if (match) return match.name;
    return '';
  };

  const cleanPhoneCode = (code?: string) => {
    if (!code) return '';
    const clean = code.trim();
    return clean.startsWith('+') ? clean : `+${clean}`;
  };

  const sanitizePhone = (num?: string): string => (num ? String(num).replace(/[^\d]/g, '') : '');

  // 1. Shipper / Pickup
  const shipper = fields?.shipper || {};
  const pickupCity = shipper.city ? String(shipper.city).trim() : '';
  const pickupState = shipper.state ? String(shipper.state).trim() : '';
  const pickupPincode = shipper.pincode !== undefined && shipper.pincode !== null ? String(shipper.pincode).trim() : '';
  const pickupCountry = inferCountry(shipper.country ? String(shipper.country) : undefined, pickupCity, pickupPincode);

  const pickup: AddressType & Record<string, any> = {
    city: pickupCity,
    state: pickupState,
    country: pickupCountry,
    pincode: pickupPincode,
    name: shipper.name ? String(shipper.name).trim() : shipper.companyName ? String(shipper.companyName).trim() : '',
    companyName: shipper.companyName ? String(shipper.companyName).trim() : '',
    line1: shipper.line1 ? String(shipper.line1).trim() : '',
    line2: shipper.line2 ? String(shipper.line2).trim() : '',
    email: shipper.email ? String(shipper.email).trim() : '',
    phoneNumber: sanitizePhone(shipper.phone?.number || shipper.phoneNumber),
    telephoneCode: cleanPhoneCode(shipper.phone?.telephoneCode || shipper.telephoneCode),
    altContactName: shipper.alternatePhone?.name || shipper.altContactName || '',
    altContactNo: sanitizePhone(shipper.alternatePhone?.number || shipper.altContactNo),
    altContactTelephoneCode: cleanPhoneCode(shipper.alternatePhone?.telephoneCode || shipper.altContactTelephoneCode),
  };

  // 2. Consignee / Destination
  const consignee = fields?.consignee || {};
  const destCity = consignee.city ? String(consignee.city).trim() : '';
  const destState = consignee.state ? String(consignee.state).trim() : '';
  const destPincode =
    consignee.pincode !== undefined && consignee.pincode !== null ? String(consignee.pincode).trim() : '';
  const destCountry = inferCountry(consignee.country ? String(consignee.country) : undefined, destCity, destPincode);

  const destination: AddressType & Record<string, any> = {
    city: destCity,
    state: destState,
    country: destCountry,
    pincode: destPincode,
    name: consignee.name
      ? String(consignee.name).trim()
      : consignee.companyName
      ? String(consignee.companyName).trim()
      : '',
    companyName: consignee.companyName ? String(consignee.companyName).trim() : '',
    line1: consignee.line1 ? String(consignee.line1).trim() : '',
    line2: consignee.line2 ? String(consignee.line2).trim() : '',
    email: consignee.email ? String(consignee.email).trim() : '',
    phoneNumber: sanitizePhone(consignee.phone?.number || consignee.phoneNumber),
    telephoneCode: cleanPhoneCode(consignee.phone?.telephoneCode || consignee.telephoneCode),
    altContactName: consignee.alternatePhone?.name || consignee.altContactName || '',
    altContactNo: sanitizePhone(consignee.alternatePhone?.number || consignee.altContactNo),
    altContactTelephoneCode: cleanPhoneCode(
      consignee.alternatePhone?.telephoneCode || consignee.altContactTelephoneCode,
    ),
  };

  // 3. Package & Shipment Type
  const pkg = fields?.package || {};
  const isDoc = typeof pkg.isDocument === 'boolean' ? pkg.isDocument : false;
  const shipmentType = isDoc ? 'document' : 'parcel';

  // 4. Weight Unit
  const uom = String(pkg.unitOfMeasurement || '').toLowerCase();
  const weightUnit: WeightUnitType = uom.includes('lb') ? 'lb' : 'kg';

  // 5. Boxes & Weight
  let parsedBoxes: BoxType[] = [];
  let hideBoxes = true;
  let hasDimensions = false;

  const rawBoxes = pkg.box || pkg.boxes || fields?.boxes || fields?.box;
  const boxesList: any[] = Array.isArray(rawBoxes)
    ? rawBoxes
    : rawBoxes && typeof rawBoxes === 'object'
    ? [rawBoxes]
    : [];

  if (boxesList.length > 0) {
    parsedBoxes = boxesList.map((b: any, index: number) => {
      const lenNum =
        b.length !== undefined && b.length !== null && b.length !== '' ? parseFloat(String(b.length)) : NaN;
      const widNum = b.width !== undefined && b.width !== null && b.width !== '' ? parseFloat(String(b.width)) : NaN;
      const hgtNum =
        b.height !== undefined && b.height !== null && b.height !== '' ? parseFloat(String(b.height)) : NaN;
      const wtNum = b.weight !== undefined && b.weight !== null && b.weight !== '' ? parseFloat(String(b.weight)) : NaN;

      const len = !isNaN(lenNum) && lenNum > 0 ? lenNum : '';
      const wid = !isNaN(widNum) && widNum > 0 ? widNum : '';
      const hgt = !isNaN(hgtNum) && hgtNum > 0 ? hgtNum : '';
      const wt = !isNaN(wtNum) && wtNum > 0 ? wtNum : '';

      if (len !== '' || wid !== '' || hgt !== '') {
        hasDimensions = true;
      }

      const boxProducts = (b.products || []).map((prod: any, prodIndex: number) => {
        const rawQty = prod.quantity ?? prod.qty ?? prod.Quantity ?? prod.Qty;
        const quantity = rawQty !== undefined && rawQty !== null && rawQty !== '' ? Number(rawQty) : null;
        const rawUnitVal = prod.unitValue ?? prod.value ?? prod.UnitValue ?? prod.Value;
        const unitValue =
          rawUnitVal !== undefined && rawUnitVal !== null && rawUnitVal !== '' ? Number(rawUnitVal) : null;
        const totalVal = quantity !== null && unitValue !== null ? quantity * unitValue : 0;
        return {
          id: prodIndex.toString(),
          description: prod.description || prod.Description || '',
          quantity,
          unitValue,
          HSNName: prod.HSNName || prod.hsnName || '',
          HSNCode: prod.HSNCode ? String(prod.HSNCode) : prod.hsnCode ? String(prod.hsnCode) : '',
          productId: prod.productId ? String(prod.productId).trim() : '',
          productIdType: prod.productIdType ? String(prod.productIdType).trim() : 'SKU',
          merchantProductId: prod.merchantProductId ? String(prod.merchantProductId).trim() : '',
          standardManufacturerProductId: prod.standardManufacturerProductId
            ? String(prod.standardManufacturerProductId).trim()
            : '',
          nonStandardManufacturerProductId: prod.nonStandardManufacturerProductId
            ? String(prod.nonStandardManufacturerProductId).trim()
            : '',
          totalValue: totalVal,
          actions: null,
        };
      });

      return {
        id: index.toString(),
        length: len,
        width: wid,
        height: hgt,
        weight: wt,
        boxCopyCount: b.boxCopyCount !== undefined && b.boxCopyCount !== null ? Number(b.boxCopyCount) : 0,
        products: boxProducts,
      };
    });
  }

  let totalWeight = 0;
  if (parsedBoxes.length > 0) {
    totalWeight = parsedBoxes.reduce((acc, b) => acc + (Number(b.weight) || 0), 0);
  }

  if (totalWeight <= 0) {
    const rawFallbackWeight = pkg.grossWeight || pkg.weight || fields?.grossWeight || fields?.weight;
    if (rawFallbackWeight !== undefined && rawFallbackWeight !== null && rawFallbackWeight !== '') {
      totalWeight = parseFloat(String(rawFallbackWeight)) || 0;
    }
  }

  if (parsedBoxes.length === 0) {
    parsedBoxes = [{ ...defaultBox, weight: totalWeight > 0 ? totalWeight : '' }];
  } else if (
    parsedBoxes.length === 1 &&
    (parsedBoxes[0].weight === '' || parsedBoxes[0].weight === 0) &&
    totalWeight > 0
  ) {
    parsedBoxes[0].weight = totalWeight;
  }

  if ((hasDimensions || parsedBoxes.length > 1) && shipmentType === 'parcel') {
    hideBoxes = false;
  }

  // Calculate volumetric & chargeable weight
  const dimensionUnit = weightUnit === 'kg' ? 'cm' : 'in';
  const volumetricSum = parsedBoxes.reduce((acc, b) => {
    const vol = calculateVolume(b.length, b.width, b.height, dimensionUnit as any);
    const wt = typeof b.weight === 'string' ? parseFloat(b.weight) || 0 : b.weight;
    return acc + (wt > vol ? wt : vol);
  }, 0);

  const chargeableWeight = volumetricSum > totalWeight ? volumetricSum : totalWeight;

  // Validate with inputRatesSchema
  const ratesInput: RatesInputType = {
    shipmentType,
    pickup,
    destination,
    pickupPinCode: pickupPincode,
    destinationPinCode: destPincode,
    weight: totalWeight,
    weightUnit,
  };

  const validationResult = inputRatesSchema.validate(ratesInput, { abortEarly: false });
  const isValid = !validationResult.error;

  const missingFields: string[] = [];
  if (validationResult.error) {
    validationResult.error.details.forEach(d => {
      const path = d.path.join('.');
      if (path.includes('pickupPinCode')) missingFields.push('Pickup Pincode');
      else if (path.includes('destinationPinCode')) missingFields.push('Destination Pincode');
      else if (path.includes('pickup.country')) missingFields.push('Pickup Country');
      else if (path.includes('destination.country')) missingFields.push('Destination Country');
      else if (path.includes('weight')) missingFields.push('Valid Weight');
      else missingFields.push(path);
    });
  }

  return {
    pickup,
    destination,
    pickupPinCode: pickupPincode,
    destinationPinCode: destPincode,
    shipmentType,
    weight: totalWeight,
    weightUnit,
    boxes: parsedBoxes,
    hideBoxes,
    boxCount: parsedBoxes.length,
    chargeableWeight,
    isValid,
    missingFields: Array.from(new Set(missingFields)),
  };
};

export const normalizeDimension = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(num) ? 0 : num;
};

export const buildRatesPayload = (
  ratesData: MappedGetRatesData,
  isTarrifPricing: boolean,
  userId?: string,
  organizationName?: string,
): RatesType & { organizationName?: boolean; userId?: string } => {
  const {
    destination,
    destinationPinCode,
    pickup,
    shipmentType,
    weight,
    weightUnit,
    pickupPinCode,
    boxes,
    chargeableWeight,
  } = ratesData;
  const finalWeight = chargeableWeight > Number(weight) ? chargeableWeight : Number(weight);

  const obj: RatesType & { organizationName?: boolean; userId?: string } = {
    destination: { ...destination, pincode: destinationPinCode?.toString() },
    source: { ...pickup, pincode: pickupPinCode.toString() },
    shipmentType,
    weight: Number(weight),
    package: {
      box: boxes.map((box, ind) => ({
        weight: Number(box.weight) < 1 && ind === 0 ? finalWeight.toString() : box.weight.toString(),
        length: normalizeDimension(box.length),
        width: normalizeDimension(box.width),
        height: normalizeDimension(box.height),
      })),
      unitOfMeasurement: weightUnit === 'kg' ? 'kg-cm' : 'lb-in',
    },
    document: shipmentType === SHIPMENT_TYPES[1].value,
    dropOff: true,
    rateFor: 'get-rates',
    organizationName: organizationName === 'Cargo Solutions Network',
  };

  if (!isTarrifPricing && userId) {
    obj.userId = userId;
  }

  return obj;
};
